<script setup lang="ts">
import type { Follower } from '@/types/profile';

const props = defineProps({
	isShow: {
		type: Boolean,
		default: false,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	followers: {
		type: Array as PropType<Array<Follower>>,
		default: () => [],
	},
	followerNumber: {
		type: Number,
		default: 0,
	},
});

const debouncedSearch = useDebouncedFn('', 500);

const rawSearch = ref('');
const selectedFollowerIds = ref<number[]>([]);
const isDialogClosing = ref(false);
const identifier = ref(0);

const isFilterActive = computed(() => debouncedSearch.value.trim().length > 0);

const emit = defineEmits<{
	(e: 'onFetchData', state: InfiniteLoadingEvent): void;
	(e: 'onSearch', search: string): void;
	(e: 'onClose'): void;
	(e: 'onSubmit', followerIds: number[]): void;
}>();

function handleFetchData($state: InfiniteLoadingEvent) {
	if (isFilterActive.value) {
		$state.complete();
		return;
	}
	emit('onFetchData', $state);
}

function handleOnFollowerChange(isSelect: boolean, userId: number) {
	if (isSelect) {
		selectedFollowerIds.value.push(userId);
	} else {
		selectedFollowerIds.value = selectedFollowerIds.value.filter((id) => id !== userId);
	}
}

function handleCloseDialog() {
	// Mark closing to suppress watchers temporarily
	isDialogClosing.value = true;

	// Reset all states
	rawSearch.value = '';
	debouncedSearch.value = '';
	selectedFollowerIds.value = [];

	// Ensure parent knows to reset search then close
	emit('onSearch', '');
	emit('onClose');

	// Allow watchers again
	isDialogClosing.value = false;
}

function handleSubmit() {
	emit('onSubmit', selectedFollowerIds.value);
	handleCloseDialog();
}

function handleReset() {
	// Reset pagination identifier and local states
	identifier.value++;
	rawSearch.value = '';
	debouncedSearch.value = '';
	selectedFollowerIds.value = [];
}

defineExpose({
	handleReset,
});

// Bridge raw input to debounced search
watch(
	() => rawSearch.value,
	(newVal) => {
		if (isDialogClosing.value) {
			return;
		}
		// If cleared, trigger immediately and sync debounced value
		if (newVal === '') {
			debouncedSearch.value = '';
			return;
		}
		// Otherwise, let debounced ref handle timing
		debouncedSearch.value = newVal;
	}
);

// Fire search when debounced value changes
watch(
	() => debouncedSearch.value,
	(val) => {
		if (isDialogClosing.value) {
			return;
		}
		emit('onSearch', val);
	}
);
</script>

<template>
	<BaseDialog class="follower-dialog" :is-show="props.isShow" @on-close="handleCloseDialog">
		<template #header>
			<UsersPlusIcon class="icon-header-confirm-dlg icon-info" :color="ICON_COLOR.PRIMARY" />
		</template>
		<template #content>
			<div class="follower-content-container">
				<div class="follower-header-container">
					<h1 class="text-lg text-semibold">Add a Co-organizer</h1>
					<p class="text-sm">
						Co-organizers can create and edit event details, manage ticket sales and view attendee
						information. If you would like to add a Co-organizer to your group, please select from
						your follower list below.
					</p>
				</div>
				<div class="follower-container">
					<div class="search-container">
						<BaseTextInput
							v-model="rawSearch"
							@keydown.esc="rawSearch = ''"
							class="search-input"
							placeholder="Search follower"
						>
							<template #leftIcon>
								<SearchIcon :color="ICON_COLOR.DEFAULT" />
							</template>
						</BaseTextInput>
					</div>
					<div class="follower-list-container">
						<FollowerItem
							v-for="follower in followers"
							:key="follower.id"
							:follower="follower"
							@on-change="handleOnFollowerChange"
						/>
						<BaseInfiniteLoading :identifier="identifier" @infinite="handleFetchData">
							<template #complete>
								<span />
							</template>
							<template #spinner>
								<VSkeletonLoader
									v-for="index in 8"
									:key="index"
									class="mx-auto"
									max-width="352"
									height="64"
									type="list-item-avatar"
								/>
							</template>
						</BaseInfiniteLoading>
					</div>
					<template v-if="followers.length === 0">
						<VSkeletonLoader
							v-if="isLoading"
							v-for="index in 8"
							:key="index"
							class="mx-auto loader-item"
							max-width="352"
							height="64"
							type="list-item-avatar"
						/>
						<div v-else class="not-found-container">
							<SearchIcon class="icon" width="24" height="24" />
							<p class="header text-md text-semibold">We couldn’t find any followers by that name.</p>
							<p class="text-sm text-regular text-center">
								Try checking the spelling or searching with a different name.
							</p>
						</div>
					</template>
					<div class="button-container">
						<BaseButton
							class="submit-btn"
							:loading="isLoading"
							:disabled="isLoading"
							@click="handleSubmit"
						>
							<template v-if="isLoading">
								<BaseProgressCircular :size="24" color="#444CE7" />
							</template>
							<template v-else>Proceed</template>
						</BaseButton>
					</div>
				</div>
			</div>
		</template>
	</BaseDialog>
</template>

<style lang="scss" scoped>
.follower-dialog {
	:deep(.content-container) {
		overflow: hidden;
	}
}

.follower-btn {
	width: fit-content;
	padding: 0;
}

.follower-content-container {
	display: flex;
	flex-direction: column;
	gap: rem(28);

	.follower-header-container {
		h1 {
			color: colors-get(gray, 900);
		}

		p {
			color: colors-get(gray, 600);
		}
	}
}

.follower-container {
	width: 100%;
	max-height: rem(512);
	display: flex;
	flex-direction: column;

	.search-container {
		position: sticky;
		top: spacings-get(1);
		z-index: 1;
		display: flex;
		justify-content: center;
		@include border-radius-default;

		.search-input {
			width: 100%;
			background-color: colors-get(base, white);

			svg {
				margin-left: spacings-get(2);
			}
		}
	}

	.follower-list-container {
		margin: spacings-get(4) 0;
	}

	.not-found-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		margin: spacings-get(10) auto;

		.header {
			margin-top: spacings-get(2);
		}
	}

	.button-container {
		position: sticky;
		bottom: 0;
		z-index: 1;
		display: flex;
		justify-content: center;

		.submit-btn {
			width: 100%;
		}
	}

	:deep(.v-skeleton-loader__avatar) {
		width: spacings-get(10);
		height: spacings-get(10);
		min-width: spacings-get(10);
		min-height: spacings-get(10);
	}
}

.loader-item {
	width: 100%;
	margin: spacings-get(2) 0;

	:deep(.v-skeleton-loader__text) {
		height: spacings-get(3);
		width: auto;
	}
}
</style>
